import { IStore } from '~/src/dash/reducers/store';
import { IntegralPlatformColors } from '~/src/dash/utils/colors';
import {
  alignRight,
  backgroundColor,
  container,
  drawerWidth,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';
import { IIASDashboardConfig } from '~/src/dash/views/ias-publisher/IASDashboard/common/types';

export const iasDashboardConfig = {
  defaultProps: {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        marginBottom: theme.spacing.unit,
      },
      alignRight,
      paper: {
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      graphPaper: {
        padding: 8,
        marginBottom: 15,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      selectedPaper: {
        ...theme.mixins.gutters(),
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      notSelectedPaper: {
        ...theme.mixins.gutters(),
      },
      dashboardCard: {
        backgroundColor: 'white',
        padding: 10,
      },
      title: {
        fontWeight: 400,
        fontSize: 12,
        color: '#292F36',
      },
      dashboardCardValue: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: 20,
        color: '#292F36',
      },
      dashboardCardComparePositive: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#4FC2B5',
      },
      dashboardCardCompareNegative: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#F05251',
      },
      densityRangeSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#6a686a',
        cursor: 'pointer',
      },
      densityRangeNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      dashboardToggleSelected: {
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
      },
      dashboardToggleNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      chartName: {
        fontSize: 16,
        fontWeight: 500,
      },
      redArrow: {
        transform: 'rotate(180deg)',
      },
      list: {
        marginTop: '20px',
        paddingLeft: '0',
        paddingTop: '0',
        paddingBottom: '0',
        marginBottom: '0',
        listStyle: 'none',
        position: 'unset',
      },
      appbar: {
        position: 'sticky',
        top: 0,
        marginLeft: 16,
        backgroundColor: primaryColor[0],
      },
      appbarTitle: {
        color: whiteColor,
        flexGrow: 1,
      },
      mainPanel: {
        position: 'relative',
        float: 'right',
        width: `calc(100vw - ${drawerWidth}px)`,
        backgroundColor,
      },
      content: {
        padding: '0',
        minHeight: 'calc(100vh - 64px)',
      },
      container,
      mainTab: {
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      mainTabSelected: {
        color: '#5D66FA',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      unselectedTimeStamp: {
        '&:hover': {
          background: '#eee 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#555555',
          fontSize: '12px',
        },
        color: '#555555',
        textDecoration: 'none',
        fontSize: '12px',
      },
      selectedTimeStamp: {
        background: '#eee 0% 0% no-repeat padding-box',
        color: '#292F36',
        fontSize: '12px',
        fontWeight: '700',
      },
      dashboardCardValueDisabled: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      paperCards: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        minHeight: 85,
      },
      paperCardsDisabled: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        backgroundColor: '#EFEFEF',
        minHeight: 85,
      },
      dashboardCardTitleDisabled: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
      },
      dashboardCardTitleDisabledLarge: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      root: {
        '.MuiTab-textColorInherit.Mui-selected': {
          color: 'white',
        },
      },
    }),
  },
  colors: IntegralPlatformColors,
  graphColors: {
    blue: '#4952FF',
    teal: '#4FC1B5',
    green: '#3FD077',
    yellow: '#FEE72F',
    red: '#FA0E0E',
    purple: '#B55DF5',
    lightBlue: '#454eff',
    lightTeal: '#51c3b5',
    lightGreen: '#3cd079',
    lightYellow: '#ffeb2f',
    lightRed: '#ff0d0d',
    lightPurple: '#b55ef8',
  },
  timezone: 'America/New_York',
  tabIndex: {
    0: 'Overview',
    1: 'Viewability',
    2: 'Invalid Traffic',
    3: 'Brand Safety',
  },
  sections: {
    Overview: {
      label: 'Overview',
      tabIndex: 0,
      cardMetrics: [
        { key: 'Total Tracked Ads', isDefaultActive: true, isDisplayed: true, isActive: true },
        { key: 'Viewable Rate', isDefaultActive: true, isDisplayed: true, isActive: true },
        { key: 'Invalid Traffic Rate', isDefaultActive: false, isDisplayed: true, isActive: false },
        { key: 'Measured Rate', isDefaultActive: false, isDisplayed: true, isActive: false },
        // { key: 'AVOC GroupM Count', isDefaultActive: false, isDisplayed: true, isActive: false },
        // { key: 'AVOC GroupM Rate', isDefaultActive: false, isDisplayed: true, isActive: false  },
        // { key: 'AVOC Count', isDefaultActive: false, isDisplayed: false, isActive: false },
        // { key: 'AVOC Rate', isDefaultActive: false, isDisplayed: false, isActive: false },
      ],
      areCardsSelectable: true,
      graph: 'graph9.sql',
      table: {
        graph: 'graph20.sql',
        groupBys: [
          {
            dataKey: 'AdvertiserName',
            label: 'Advertisers',
            paramKey: 'Advertisers',
            idKey: 'AdvertiserID',
            childKey: 'Orders',
            nodeLevel: 0,
            isDefault: true,
            subheader: 'Delivery',
          },
          {
            dataKey: 'OrderName',
            label: 'Orders',
            paramKey: 'Orders',
            idKey: 'OrderID',
            childKey: 'Line_Items',
            nodeLevel: 1,
            subheader: 'Delivery',
          },
          {
            dataKey: 'LineItemName',
            label: 'Line Items',
            paramKey: 'Line_Items',
            idKey: 'LineItemID',
            childKey: 'Creatives',
            nodeLevel: 2,
            subheader: 'Delivery',
          },
          {
            dataKey: 'CreativeName',
            label: 'Creatives',
            paramKey: 'Creatives',
            idKey: 'CreativeID',
            nodeLevel: 3,
            subheader: 'Delivery',
          },
          {
            dataKey: 'AdUnitName',
            label: 'Ad Units',
            paramKey: 'Ad_Units',
            idKey: 'AdUnitID',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'AdSize',
            label: 'Sizes',
            paramKey: 'Sizes',
            idKey: 'AdSize',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom1Name',
            label: 'Traffic Sources',
            paramKey: 'Traffic_Sources',
            idKey: 'Custom1',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom2Name',
            label: 'Tiles',
            paramKey: 'Tiles',
            idKey: 'Custom2',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom3Name',
            label: 'Sections',
            paramKey: 'Sections',
            idKey: 'Custom3',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
        ],
        columns: [
          { dataKey: 'TotalImps', label: 'Total Tracked Ads', renderType: 'bignumber' },
          { dataKey: 'RenderedImps', label: 'Impressions (Rendered)', renderType: 'bignumber' },
          { dataKey: 'RefreshImps', label: 'Refreshed Ads', renderType: 'bignumber' },
          { dataKey: 'MeasuredRate', label: 'Measured Rate', renderType: 'percent' },
          {
            dataKey: 'ViewabilityRate',
            label: 'Viewable Rate (Out Of Measured Ads)',
            renderType: 'percent',
          },
          { dataKey: 'IVTRate', label: 'Invalid Traffic Rate', renderType: 'percent' },
        ],
      },
    },
    Viewability: {
      label: 'Viewability',
      tabIndex: 1,
      cardMetrics: [
        { key: 'Viewable Rate', isDefaultActive: true },
        { key: 'Total Tracked Ads', isDefaultActive: false },
        { key: 'Viewable Impressions', isDefaultActive: false },
        { key: 'Non-Viewable Ads', isDefaultActive: false },
      ],
      graph: 'graph10.sql',
      secondaryCharts: [
        {
          title: 'Out Of View Reasons',
          type: 'horizontalBar',
          graph: 'graph16.sql',
          barChartDataItems: {
            MrcOutOfViewNETImps: 'Not Enough Time',
            MrcOutOfViewNEPImps: 'Not Enough Pixels',
            MrcOutOfViewNSImps: 'Video Never Started',
            MrcOutOfViewOOFImps: 'Out Of Focus',
            MrcOutOfViewNIVImps: 'Never On Screen',
            MrcOutOfViewUNKImps: 'Other',
          },
        },
        {
          title: 'Viewability By Device Type',
          type: 'pie',
          graph: 'graph13.sql',
          pieChartDataItems: {
            groupedByKey: 'MediaTypeID',
            valueKey: 'ViewableImpressions',
          },
        },
      ],
      table: {
        graph: 'graph21.sql',
        groupBys: [
          {
            dataKey: 'AdvertiserName',
            label: 'Advertisers',
            paramKey: 'Advertisers',
            idKey: 'AdvertiserID',
            childKey: 'Orders',
            nodeLevel: 0,
            isDefault: true,
            subheader: 'Delivery',
          },
          {
            dataKey: 'OrderName',
            label: 'Orders',
            paramKey: 'Orders',
            idKey: 'OrderID',
            childKey: 'Line_Items',
            nodeLevel: 1,
            subheader: 'Delivery',
          },
          {
            dataKey: 'LineItemName',
            label: 'Line Items',
            paramKey: 'Line_Items',
            idKey: 'LineItemID',
            childKey: 'Creatives',
            nodeLevel: 2,
            subheader: 'Delivery',
          },
          {
            dataKey: 'CreativeName',
            label: 'Creatives',
            paramKey: 'Creatives',
            idKey: 'CreativeID',
            nodeLevel: 3,
            subheader: 'Delivery',
          },
          {
            dataKey: 'AdUnitName',
            label: 'Ad Units',
            paramKey: 'Ad_Units',
            idKey: 'AdUnitID',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'AdSize',
            label: 'Sizes',
            paramKey: 'Sizes',
            idKey: 'AdSize',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom1Name',
            label: 'Traffic Sources',
            paramKey: 'Traffic_Sources',
            idKey: 'Custom1',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom2Name',
            label: 'Tiles',
            paramKey: 'Tiles',
            idKey: 'Custom2',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom3Name',
            label: 'Sections',
            paramKey: 'Sections',
            idKey: 'Custom3',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
        ],
        columns: [
          { dataKey: 'TotalImps', label: 'Total Tracked Ads', renderType: 'bignumber' },
          { dataKey: 'MeasuredRate', label: 'Measured Rate', renderType: 'percent' },
          { dataKey: 'MeasuredImps', label: 'Measured Ads', renderType: 'bignumber' },
          {
            dataKey: 'ViewabilityRate',
            label: 'Viewable Rate (Out Of Measured Ads)',
            renderType: 'percent',
          },
          {
            dataKey: 'ViewableImpressions',
            label: 'Viewable Impressions',
            renderType: 'bignumber',
          },
        ],
      },
    },
    'Invalid Traffic': {
      label: 'Invalid Traffic',
      tabIndex: 2,
      cardMetrics: [
        { key: 'Invalid Traffic Rate', isDefaultActive: true },
        { key: 'Total Tracked Ads', isDefaultActive: false },
        { key: 'Invalid Traffic Ads', isDefaultActive: false },
        { key: 'Valid Traffic', isDefaultActive: false },
      ],
      graph: 'graph11.sql',
      secondaryCharts: [
        {
          title: 'Invalid Traffic By Type',
          type: 'horizontalBar',
          graph: 'graph17.sql',
          isRate: true,
          barChartDataItems: {
            FraudGIVTRate: 'General Invalid Traffic (GIVT)',
            FraudNHTRate: 'Bots (SIVT)',
            FraudHARate: 'Hidden Ads (SIVT)',
            FraudDSRate: 'Domain Spoofing (SIVT)',
          },
        },
        {
          title: 'Invalid Traffic By Device Type',
          type: 'pie',
          graph: 'graph14.sql',
          pieChartDataItems: {
            groupedByKey: 'MediaTypeID',
            valueKey: 'SuspiciousImps',
          },
        },
      ],
      table: {
        graph: 'graph22.sql',
        groupBys: [
          {
            graph: 'graph21.sql',
            dataKey: 'AdvertiserName',
            label: 'Advertisers',
            paramKey: 'Advertisers',
            idKey: 'AdvertiserID',
            childKey: 'Orders',
            nodeLevel: 0,
            isDefault: true,
            subheader: 'Delivery',
          },
          {
            dataKey: 'OrderName',
            label: 'Orders',
            paramKey: 'Orders',
            idKey: 'OrderID',
            childKey: 'Line_Items',
            nodeLevel: 1,
            subheader: 'Delivery',
          },
          {
            dataKey: 'LineItemName',
            label: 'Line Items',
            paramKey: 'Line_Items',
            idKey: 'LineItemID',
            childKey: 'Creatives',
            nodeLevel: 2,
            subheader: 'Delivery',
          },
          {
            dataKey: 'CreativeName',
            label: 'Creatives',
            paramKey: 'Creatives',
            idKey: 'CreativeID',
            nodeLevel: 3,
            subheader: 'Delivery',
          },
          {
            dataKey: 'AdUnitName',
            label: 'Ad Units',
            paramKey: 'Ad_Units',
            idKey: 'AdUnitID',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'AdSize',
            label: 'Sizes',
            paramKey: 'Sizes',
            idKey: 'AdSize',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom1Name',
            label: 'Traffic Sources',
            paramKey: 'Traffic_Sources',
            idKey: 'Custom1',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom2Name',
            label: 'Tiles',
            paramKey: 'Tiles',
            idKey: 'Custom2',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom3Name',
            label: 'Sections',
            paramKey: 'Sections',
            idKey: 'Custom3',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
        ],
        columns: [
          { dataKey: 'TotalImps', label: 'Total Tracked Ads', renderType: 'bignumber' },
          { dataKey: 'IVTRate', label: 'Invalid Traffic Rate', renderType: 'percent' },
          { dataKey: 'SuspiciousImps', label: 'Invalid Traffic Ads', renderType: 'bignumber' },
          { dataKey: 'UnsuspiciousImps', label: 'Valid Traffic', renderType: 'bignumber' },
          {
            dataKey: 'BlockedSuspiciousImps',
            label: 'Blocked Invalid Traffic',
            renderType: 'bignumber',
          },
        ],
      },
    },
    'Brand Safety': {
      label: 'Brand Safety',
      tabIndex: 3,
      cardMetrics: [
        { key: 'Blocked Rate', isDefaultActive: true },
        { key: 'Total Tracked Ads', isDefaultActive: false },
        { key: 'Blocked Ads', isDefaultActive: false },
        { key: 'Passed Ads', isDefaultActive: false },
      ],
      graph: 'graph12.sql',
      secondaryCharts: [
        {
          title: 'Blocked Reasons',
          type: 'horizontalBar',
          graph: 'graph18.sql',
          isRate: true,
          barChartDataItems: {
            BlockedSuitabilityRate: 'Brand Suitability',
            BlockedContentRate: 'Content',
            BlockedCustomListRate: 'Custom List',
            BlockedGeoRate: 'Geo',
            BlockedSuspiciousRate: 'Invalid Traffic',
            BlockedKeywordRate: 'Keywords',
            BlockedSeeThroughRate: 'See-Through Rate',
            BlockedLanguageRate: 'Language Mismatch',
          },
        },
        {
          title: 'Blocking By Device Type',
          type: 'pie',
          graph: 'graph15.sql',
          pieChartDataItems: {
            groupedByKey: 'MediaTypeID',
            valueKey: 'BlockedImps',
          },
        },
      ],
      table: {
        graph: 'graph23.sql',
        groupBys: [
          {
            dataKey: 'AdvertiserName',
            label: 'Advertisers',
            paramKey: 'Advertisers',
            idKey: 'AdvertiserID',
            childKey: 'Orders',
            nodeLevel: 0,
            isDefault: true,
            subheader: 'Delivery',
          },
          {
            dataKey: 'OrderName',
            label: 'Orders',
            paramKey: 'Orders',
            idKey: 'OrderID',
            childKey: 'Line_Items',
            nodeLevel: 1,
            subheader: 'Delivery',
          },
          {
            dataKey: 'LineItemName',
            label: 'Line Items',
            paramKey: 'Line_Items',
            idKey: 'LineItemID',
            childKey: 'Creatives',
            nodeLevel: 2,
            subheader: 'Delivery',
          },
          {
            dataKey: 'CreativeName',
            label: 'Creatives',
            paramKey: 'Creatives',
            idKey: 'CreativeID',
            nodeLevel: 3,
            subheader: 'Delivery',
          },
          {
            dataKey: 'AdUnitName',
            label: 'Ad Units',
            paramKey: 'Ad_Units',
            idKey: 'AdUnitID',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'AdSize',
            label: 'Sizes',
            paramKey: 'Sizes',
            idKey: 'AdSize',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom1Name',
            label: 'Traffic Sources',
            paramKey: 'Traffic_Sources',
            idKey: 'Custom1',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom2Name',
            label: 'Tiles',
            paramKey: 'Tiles',
            idKey: 'Custom2',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
          {
            dataKey: 'Custom3Name',
            label: 'Sections',
            paramKey: 'Sections',
            idKey: 'Custom3',
            nodeLevel: 0,
            subheader: 'Inventory',
          },
        ],
        columns: [
          { dataKey: 'TotalImps', label: 'Total Tracked Ads', renderType: 'bignumber' },
          { dataKey: 'AdBlockRate', label: 'Blocked Rate', renderType: 'percent' },
          { dataKey: 'BlockedImps', label: 'Blocked Ads', renderType: 'bignumber' },
          { dataKey: 'FailedAdRate', label: 'Failed Rate', renderType: 'percent' },
          { dataKey: 'FailedImps', label: 'Failed Ads', renderType: 'bignumber' },
        ],
      },
    },
  },
  filters: {
    Formats: {
      menuTitle: 'Formats',
      list: {
        '001': {
          ID: '001',
          title: 'Display',
          selected: false,
          values: ['111', '221', '121', '231', '131'],
        },
        '002': {
          ID: '002',
          title: 'Video',
          selected: false,
          values: ['112', '222', '122', '232', '132'],
        },
      },
    },
    'Device Types': {
      menuTitle: 'Device Types',
      list: {
        101: {
          ID: '101',
          title: 'Desktop',
          selected: false,
          values: ['111', '112'],
        },
        102: {
          ID: '102',
          title: 'Mobile',
          selected: false,
          values: ['221', '222', '121', '122'],
        },
        103: {
          ID: '103',
          title: 'Tablet',
          selected: false,
          values: ['231', '232', '131', '132'],
        },
      },
    },
  },
  metrics: {
    'Total Tracked Ads': {
      label: 'Total Tracked Ads',
      dataKey: 'TrackedAds',
      compareKey: 'CompareTrackedAds',
      color: IntegralPlatformColors.midnight,
      dataType: 'integer',
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: false,
      calcType: 'sum',
    },
    'Viewable Rate': {
      label: 'Viewable Rate (Out Of Measure Ads)',
      dataKey: 'ViewabilityRate',
      compareKey: 'CompareViewabilityRate',
      color: IntegralPlatformColors.goldenrod,
      dataType: 'percent',
      yAxisID: 'rate',
      chartType: 'line',
      isInverseMetric: false,
      calcType: 'rate',
      rateElements: {
        numeratorKeys: ['ViewableImpressions'],
        denominatorKeys: ['MeasuredImps'],
      },
      relatedMetricsKeys: ['Viewable Impressions', 'Measured Impressions'],
    },
    'Invalid Traffic Rate': {
      label: 'Invalid Traffic Rate',
      dataKey: 'IVTRate',
      compareKey: 'CompareIVTRate',
      color: IntegralPlatformColors.indigo,
      dataType: 'percent',
      yAxisID: 'rate',
      chartType: 'line',
      isInverseMetric: true,
      calcType: 'rate',
      rateElements: {
        numeratorKeys: ['SuspiciousImps'],
        denominatorKeys: ['TrackedAds'],
      },
      relatedMetricsKeys: ['Invalid Traffic Ads', 'Total Tracked Ads'],
    },
    'Measured Rate': {
      label: 'Measured Rate',
      dataKey: 'MeasuredRate',
      compareKey: 'CompareMeasuredRate',
      dataType: 'percent',
      color: IntegralPlatformColors.turquoise,
      yAxisID: 'rate',
      chartType: 'line',
      isInverseMetric: false,
      calcType: 'rate',
      rateElements: {
        numeratorKeys: ['MeasuredImps'],
        denominatorKeys: ['MeasuredImps', 'UnmeasuredImps'],
      },
      relatedMetricsKeys: ['Measured Impressions', 'Unmeasured Impressions'],
    },
    'Blocked Rate': {
      label: 'Blocked Rate',
      dataKey: 'AdBlockRate',
      compareKey: 'CompareAdBlockedRate',
      dataType: 'percent',
      color: IntegralPlatformColors.indigo,
      yAxisID: 'rate',
      chartType: 'line',
      isInverseMetric: true,
      calcType: 'rate',
      rateElements: {
        numeratorKeys: ['BlockedImps'],
        denominatorKeys: ['MobileGrossImps'],
      },
      relatedMetricsKeys: ['Blocked Ads', 'Mobile Gross Impressions'],
    },
    'Viewable Impressions': {
      label: 'Viewable Impressions',
      dataKey: 'ViewableImpressions',
      compareKey: 'CompareViewableImpressions',
      dataType: 'integer',
      color: IntegralPlatformColors.indigo,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: false,
      calcType: 'sum',
    },
    'Measured Impressions': {
      label: 'Measured Impressions',
      dataKey: 'MeasuredImps',
      compareKey: 'CompareMeasuredImps',
      dataType: 'integer',
    },
    'Invalid Traffic Ads': {
      label: 'Invalid Traffic Ads',
      dataKey: 'SuspiciousImps',
      compareKey: 'CompareSuspiciousImps',
      dataType: 'integer',
      color: IntegralPlatformColors.goldenrod,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: true,
      calcType: 'sum',
    },
    'Valid Traffic': {
      label: 'Valid Traffic',
      dataKey: 'UnsuspiciousImps',
      compareKey: 'CompareUnsuspiciousImps',
      dataType: 'integer',
      color: IntegralPlatformColors.turquoise,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: false,
      calcType: 'sum',
    },
    'Unmeasured Impressions': {
      label: 'Unmeasured Impressions',
      dataKey: 'UnmeasuredImps',
      compareKey: 'CompareUnmeasuredImps',
      dataType: 'integer',
    },
    'Non-Viewable Ads': {
      label: 'Non-Viewable Ads',
      dataKey: 'UnviewableImps',
      compareKey: 'CompareUnviewableImps',
      dataType: 'integer',
      color: IntegralPlatformColors.turquoise,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: false,
      calcType: 'sum',
    },
    'Blocked Ads': {
      label: 'Blocked Ads',
      dataKey: 'BlockedImps',
      compareKey: 'CompareBlockedImps',
      dataType: 'integer',
      color: IntegralPlatformColors.turquoise,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: true,
      calcType: 'sum',
    },
    'Passed Ads': {
      label: 'Passed Ads',
      dataKey: 'PassedAds',
      compareKey: 'ComparePassedAds',
      dataType: 'integer',
      color: IntegralPlatformColors.goldenrod,
      yAxisID: 'integer',
      chartType: 'bar',
      isInverseMetric: false,
      calcType: 'sum',
    },
    'Mobile Gross Impressions': {
      label: 'Mobile Gross Impressions',
      dataKey: 'MobileGrossImps',
      compareKey: 'CompareMobileGrossImps',
      dataType: 'integer',
    },
    'Rendered Impressions': {
      label: 'Rendered Impressions',
      dataKey: 'RenderedImps',
      compareKey: 'CompareRenderedImps',
      dataType: 'integer',
    },
    // 'AVOC GroupM Count': {
    //   label: 'AVOC GroupM Count',
    //   dataKey: 'AudibleViewableCustomQ2',
    //   compareKey: 'CompareAudibleViewableCustomQ2',
    //   color: IntegralPlatformColors.darkGray,
    //   dataType: 'integer',
    //   yAxisID: 'integer',
    //   chartType: 'bar',
    //   isInverseMetric: false,
    //   calcType: 'sum',
    // },
    // 'AVOC GroupM Rate': {
    //   label: 'AVOC GroupM Rate',
    //   dataKey: 'AVOCGroupMRate',
    //   compareKey: 'CompareAVOCGroupMRate',
    //   color: IntegralPlatformColors.red,
    //   dataType: 'percent',
    //   yAxisID: 'rate',
    //   chartType: 'line',
    //   isInverseMetric: false,
    //   calcType: 'rate',
    //   rateElements: {
    //     numeratorKeys: ['AudibleViewableCustomQ2'],
    //     denominatorKeys: ['RenderedImps'],
    //   },
    //   relatedMetricsKeys: [
    //     'AVOC GroupM Count',
    //     'Rendered Impressions',
    //   ],
    // },
    // 'AVOC Count': {
    //   label: 'AVOC Count',
    //   dataKey: 'AudibleViewableIndStdQ4',
    //   compareKey: 'CompareAudibleViewableIndStdQ4',
    //   color: IntegralPlatformColors.orchid,
    //   dataType: 'integer',
    //   yAxisID: 'integer',
    //   chartType: 'bar',
    //   isInverseMetric: false,
    //   calcType: 'sum',
    // },
    // 'AVOC Rate': {
    //   label: 'AVOC Rate',
    //   dataKey: 'AVOCRate',
    //   compareKey: 'CompareAVOCRate',
    //   color: IntegralPlatformColors.yellow,
    //   dataType: 'percent',
    //   yAxisID: 'rate',
    //   chartType: 'line',
    //   isInverseMetric: false,
    //   calcType: 'rate',
    //   rateElements: {
    //     numeratorKeys: ['AudibleViewableIndStdQ4'],
    //     denominatorKeys: ['RenderedImps'],
    //   },
    //   relatedMetricsKeys: [
    //     'AVOC Count',
    //     'Rendered Impressions',
    //   ],
    // },
  },
} as IIASDashboardConfig;
